import type { Gender } from '../../components/landing/quiz/constants';
import { getSubjectPronoun } from '../../components/landing/quiz/constants';

/**
 * Word-for-word copy for the board progress card (THE-946 spec, "Copy, word
 * for word" + "Look and feel" § pronouns). Every line here is quoted directly
 * from the spec — do not reword, shorten, or add new lines (spec, "Words and
 * tracking").
 *
 * PRONOUNS NEVER HARD-CODE "her". Every string below is built from `gender`
 * and `isMe`, never from the recipient's name or relationship label. `isMe`
 * wins over gender when both are present — the spec's own board gets "you" /
 * "your" and a different title, never "they"/"their" even when no gender is
 * on file for the shopper's self-entry.
 */

/** Object-pronoun form ("Getting to know HER/HIM/THEM/YOU"). Distinct from
 *  `getPossessivePronoun` (her/his/their) — "him" and "his" differ, so the
 *  existing quiz helper can't be reused for this slot. */
function objectPronoun(gender: Gender | null): 'her' | 'him' | 'them' {
  if (gender === 'female') return 'her';
  if (gender === 'male') return 'him';
  return 'them';
}

export interface BoardProgressPronouns {
  /** "she" / "he" / "they" / "you" */
  subject: string;
  /** "her" / "him" / "them" / "you" — object form for "Getting to know ___". */
  object: string;
}

export function resolveBoardProgressPronouns(
  gender: Gender | null,
  isMe: boolean,
): BoardProgressPronouns {
  if (isMe) return { subject: 'you', object: 'you' };
  return { subject: getSubjectPronoun(gender), object: objectPronoun(gender) };
}

/** "she'd" / "he'd" / "they'd" / "you'd" — used only in "what ___'d love". */
function contractedWould(subject: string): string {
  return `${subject}'d`;
}

export interface BoardProgressCopy {
  stage1Title: string;
  stage1Subtitle: string;
  stage2Title: string;
  stage3Title: string;
  stage3Subtitle: string;
  savesEmptyCopy: string;
  savesCountCopy: (heartCount: number) => string;
  countOfTenCopy: (count: number) => string;
  /**
   * V2 Friday band/sticker label, and the stage-4 title (every width, every
   * viewer) — the canonical feature name (THE-1047 spec, "One name for the
   * feature"). Pronoun-free, hence also the module constant below.
   */
  fridayBandTitle: string;
  /** `fridayBandTitle` with the sticker's line break — see
   *  `BOARD_FRIDAY_BAND_TITLE_LINES`. */
  fridayBandTitleLines: string;
  /** Stage-4 title. Same words as `fridayBandTitle`, same on desktop and
   *  phone, signed in or out (THE-1047 spec, "Same words on desktop and
   *  phone. Only the layout changes"). */
  stage4Title: string;
  /** Stage-4 subtitle — "New gift ideas each week", the SAME words for
   *  every pronoun and for `isMe` (THE-1304, Kate 2026-10-28, row 147,
   *  confirmed for phone and desktop). Was "Her whole board refreshes each
   *  week" (pronoun-built, THE-968 call 2's no-trailing-period rule). */
  stage4Subtitle: string;
  /** Stage-4 subtitle, viewer with NO account. THE-1304 (Kate 2026-10-28):
   *  the SAME "New gift ideas each week" line as `stage4Subtitle` now — no
   *  longer a distinct signed-out wording. Was "Her whole board, refreshed
   *  each week" (a different comma/tense from the signed-in line). */
  stage4SubtitleSignedOut: string;
}

/**
 * "Fresh Find Fridays" — THE canonical name for the feature (THE-1047 spec,
 * "One name for the feature": *"Today the app says 'Fresh Find Fridays',
 * 'Fresh finds every Friday' and 'Get fresh finds every Friday' in different
 * places. The header and celebration use 'Fresh Find Fridays' as the
 * name."*). Used for the V2 band/sticker label and the stage-4 title, on
 * every width and whether or not the viewer has an account — there is no
 * second wording left to pick between. Same string `FreshFindFridaysCard`
 * exports as `FRESH_FIND_TITLE` for the People-page tag; not re-exported from
 * there because this module intentionally stays independent of that file's
 * flag-reading surface (see this file's docblock and `BoardProgressCard`'s
 * "THIS COMPONENT STAYS FLAG-BLIND" note) — a shared string, imported from
 * neither, duplicated in both, with a test in each file pinning the words.
 * Pronoun-free, so it is a constant rather than a `buildBoardProgressCopy`
 * computation, for the same reason `BOARD_SAVES_EMPTY_COPY` is: surfaces that
 * have no `gender`/`isMe` of their own (the band on a shared board view) still
 * need the words. The `✦` is NOT part of the string — it is drawn as its own
 * element so it can be coloured and hidden from screen readers, exactly as
 * `FreshFindFridaysCard` already does with `FRESH_FIND_TITLE`.
 */
export const BOARD_FRIDAY_BAND_TITLE = 'Fresh Find Fridays';

/**
 * The SAME label with the line-break opportunity the desktop sticker needs
 * (THE-968 call 1). The sticker is two lines — "Fresh Find" / "Fridays" —
 * while the mobile band is one; one string serves both because the band is
 * `white-space: normal` below 1024px (the newline collapses to a space) and
 * `pre-line` above it (the newline is honoured). Rendering two separate text
 * nodes instead would split the label in the accessibility tree and duplicate
 * the words in the DOM; a second constant would let the two wordings drift.
 * `boardProgressCopy.test.ts` pins that collapsing this yields
 * `BOARD_FRIDAY_BAND_TITLE` exactly.
 */
export const BOARD_FRIDAY_BAND_TITLE_LINES = 'Fresh Find\nFridays';

/** The V2 Unlock pill's visible copy (THE-968: "(lock) Sign in"). */
export const BOARD_FRIDAY_UNLOCK_LABEL = '🔒 Sign in';

/**
 * The pill's full accessible name (THE-968: kept as full sentence even though
 * the visible label shortened to "Sign in"). "Sign in" alone, read by a screen
 * reader without the Friday context, doesn't say what it unlocks. Uses the
 * canonical name (THE-1047), same wording `FreshFindFridaysCard` pins as
 * `FRESH_FIND_SIGNIN_ARIA_LABEL`.
 */
export const BOARD_FRIDAY_UNLOCK_ARIA_LABEL =
  'Sign in to unlock Fresh Find Fridays';

/** "Your saves live here" (0-saves dashed placeholder). Pronoun-free, so it's
 *  also reused directly by the desktop pinned header (THE-949 / PR 3), which
 *  has no gender/isMe of its own to build a full `BoardProgressCopy` from. */
export const BOARD_SAVES_EMPTY_COPY = 'Your saves live here';

/**
 * THE-1049 combined big-header card (PDF "Board header - one calm header",
 * pages 2-6). This is the NEW header's copy, kept separate from the fields
 * above rather than folded into `BoardProgressCopy`/`buildBoardProgressCopy`:
 * the two components render side by side during the rollout (the old
 * `BoardProgressCard` 4-stage design is what THE-1044 retires), and several
 * strings below are flatly different words for what looks like the same slot
 * — the empty-saves placeholder reads "0 saves" here, not "Your saves live
 * here", and the signed-out "from then on" line is a sign-in LINK with its
 * own sentence, not `stage4SubtitleSignedOut`. Reusing the old field names
 * would either misname what they hold or silently reword the old card too.
 *
 * The progress-tile TITLE has no separate first-visit string: the mock gives
 * stages 1 and 2 ("nothing yet" and "1 to 9 reactions") the exact same
 * "Getting to know ___" line, which is already `stage2Title` above — that
 * field is reused as-is rather than duplicated under a new name.
 */

/** The ♥/✕ legend under the progress bar — the ticket's wording change
 *  ("♥ to save · ✕ to remove", was "♥ the keepers, ✕ the misses") applies at
 *  EVERY width (Kate, 2026-10-26: supersedes the earlier shepherd-review call
 *  that scoped the reword to desktop only — mobile now drops its old "the
 *  keepers, the misses" wording too). One constant, one wording, read by the
 *  component's single `Legend` at every breakpoint. The ♥ and ✕ are drawn as
 *  separate coloured glyphs in the component (so they can carry the bar's own
 *  pink/grey), so this constant holds only the words either side of them; the
 *  "·" separator lives on the end of the KEEPERS half so it sits between the
 *  two halves once rendered. */
export const BOARD_HEADER_LEGEND_KEEPERS = 'to save ·';
export const BOARD_HEADER_LEGEND_MISSES = 'to remove';

/** The legend's accessible name (PDF p.7, "For screen readers") — deliberately
 *  NOT the visible text (which leads with the glyphs), because "heart to
 *  save" isn't a sentence. THE-1186: reworded to match the new visible copy,
 *  at every width (see `BOARD_HEADER_LEGEND_KEEPERS` above). */
export const BOARD_HEADER_LEGEND_ARIA = 'Heart to save, X to remove';

/** "0 saves" — the big header's empty-saves placeholder (PDF p.2), a
 *  different string from `BOARD_SAVES_EMPTY_COPY` (see module doc above). */
export const BOARD_HEADER_SAVES_EMPTY_COPY = '0 saves';

/** The 10th-reaction celebration title, signed in, at the top of the board
 *  (PDF p.3-4) — addressed to the shopper ("You"), not built from the
 *  recipient's pronoun, so it is a constant rather than a
 *  `buildBoardProgressCopy` field. Same words whether this is the first
 *  sight of the celebration or a later render of it (THE-1051 owns *when*
 *  this shows, not what it says). */
export const BOARD_HEADER_CELEBRATION_TITLE = 'You unlocked Fresh Find Fridays';

/**
 * THE-1298 — the full-screen/dialog moment that REPLACES the signed-in toast
 * at the 10th reaction for a permanent-account viewer with Fresh Find
 * Fridays on. Word for word from the ticket's "Copy, word for word" — do not
 * reword. The ✦ after the title is drawn separately (aria-hidden), same
 * convention as `BOARD_FRIDAY_BAND_TITLE`'s own ✦.
 */
export const BOARD_FRESH_FIND_MOMENT_EYEBROW = '10 of 10 ✓';

/** `{Name}, decoded` / isMe: `You, decoded`. */
export function boardFreshFindMomentTitle(recipientName: string, isMe: boolean): string {
  return isMe ? 'You, decoded' : `${recipientName}, decoded`;
}

/** The Friday card's label — the SAME canonical name as `BOARD_FRIDAY_BAND_TITLE`,
 *  shown in capitals via CSS `text-transform: uppercase` at the call site, not
 *  typed in capitals here. */
export const BOARD_FRESH_FIND_MOMENT_CARD_LABEL = BOARD_FRIDAY_BAND_TITLE;

/** "New ideas for her/him/them, every Friday" — isMe falls out of
 *  `resolveBoardProgressPronouns` resolving to "you" with no special case
 *  needed here. */
export function boardFreshFindMomentCardLine(gender: Gender | null, isMe: boolean): string {
  const { object } = resolveBoardProgressPronouns(gender, isMe);
  return `New ideas for ${object}, every Friday`;
}

/** THE-1298 scope change (Kate, 2026-10-28) — the moment's only action.
 *  The earlier "Want this week's now?" / "Yes, show me" / "Later" footer is
 *  gone: there is no early-drop regenerate path yet (tracked separately,
 *  sheet IDs 312/316), so Okay just closes the moment. */
export const BOARD_FRESH_FIND_MOMENT_OKAY = 'Okay';
export const BOARD_FRESH_FIND_MOMENT_CLOSE_ARIA = 'Close';

/**
 * THE-1299 — the signed-out audience's footer. The primary button's VISIBLE
 * label is `BOARD_FRIDAY_UNLOCK_LABEL` itself (the same "🔒 Sign in" words
 * as the header's own door, reused rather than duplicated so the two can
 * never read differently) — see `BoardFreshFindMoment`'s footer. Only the
 * accessible name and the secondary "Later" control are new strings.
 */
export const BOARD_FRESH_FIND_MOMENT_LATER = 'Later';

/** "Sign in to get fresh ideas for {Name} every Friday" / isMe: "...for you
 *  every Friday" — the signed-out primary button's full accessible name
 *  (ticket: "Sign in" alone doesn't say what it unlocks, same reasoning as
 *  `BOARD_FRIDAY_UNLOCK_ARIA_LABEL`). */
export function boardFreshFindMomentSignInAria(recipientName: string, isMe: boolean): string {
  return `Sign in to get fresh ideas for ${isMe ? 'you' : recipientName} every Friday`;
}

/** "♥ N saved". Pronoun-free — see `BOARD_SAVES_EMPTY_COPY`. */
export function boardSavesCountCopy(heartCount: number): string {
  return `♥ ${boardSavesCountWords(heartCount)}`;
}

/** "N saved" — the words of `boardSavesCountCopy` without the decorative ♥,
 *  which a screen reader would otherwise announce as "black heart suit". */
function boardSavesCountWords(heartCount: number): string {
  return `${heartCount} saved`;
}

/**
 * Accessible name for a saves control whose visible label is
 * `BOARD_SAVES_EMPTY_COPY` / `boardSavesCountCopy` — "14 saved, open saved
 * gifts for Mom", or "Your saves live here, open saved gifts for Mom" at 0.
 * Leads with the visible words (WCAG 2.5.3, Label in Name) so the count isn't
 * hidden from a screen reader behind a fixed "Open saved gifts for {name}",
 * and a voice-control user can say what they see.
 */
export function boardSavesAccessibleName(heartCount: number, recipientName: string): string {
  const visible = heartCount === 0 ? BOARD_SAVES_EMPTY_COPY : boardSavesCountWords(heartCount);
  return `${visible}, open saved gifts for ${recipientName}`;
}

/**
 * Same shape as `boardSavesAccessibleName`, but for the big header
 * (`BoardHeaderCard`), whose empty-state visible text is `BOARD_HEADER_SAVES_EMPTY_COPY`
 * ("0 saves"), not the old card's `BOARD_SAVES_EMPTY_COPY` ("Your saves live
 * here"). Reusing `boardSavesAccessibleName` here would announce words the
 * header never shows, breaking Label in Name for voice control.
 */
export function boardHeaderSavesAccessibleName(heartCount: number, recipientName: string): string {
  const visible = heartCount === 0 ? BOARD_HEADER_SAVES_EMPTY_COPY : boardSavesCountWords(heartCount);
  return `${visible}, open saved gifts for ${recipientName}`;
}

/**
 * Builds every line the card needs for one recipient. `recipientName` is
 * used exactly as the shopper typed it (spec: "never shortened") and is
 * irrelevant for `isMe` boards, whose titles don't use a name at all.
 */
export function buildBoardProgressCopy(
  recipientName: string,
  gender: Gender | null,
  isMe: boolean,
): BoardProgressCopy {
  const { subject, object } = resolveBoardProgressPronouns(gender, isMe);
  const possessiveName = isMe ? 'Your' : `${recipientName}'s`;

  return {
    stage1Title: isMe
      ? 'Your board learns from you'
      : `${recipientName}'s board learns from you`,
    stage1Subtitle: `♡ what ${contractedWould(subject)} love, ✕ what ${subject} wouldn't`,
    stage2Title: `Getting to know ${object}`,
    stage3Title: `You've taught ${isMe ? 'your' : possessiveName} board a lot`,
    stage3Subtitle: 'Keep going — every ♡ and ✕ still helps',
    savesEmptyCopy: BOARD_SAVES_EMPTY_COPY,
    savesCountCopy: boardSavesCountCopy,
    countOfTenCopy: (count: number) => `${count} of 10`,
    fridayBandTitle: BOARD_FRIDAY_BAND_TITLE,
    fridayBandTitleLines: BOARD_FRIDAY_BAND_TITLE_LINES,
    stage4Title: BOARD_FRIDAY_BAND_TITLE,
    // THE-1304 (Kate 2026-10-28, row 147): one pronoun-free line, signed in
    // AND signed out — supersedes THE-968's separate, pronoun-built pair.
    stage4Subtitle: 'New gift ideas each week',
    stage4SubtitleSignedOut: 'New gift ideas each week',
  };
}
